import * as React from "react";
import {
  AccessReportsAdminAddProductAddSpecialties,
  EditSpecialtyName,
  ManageOptions,
  ServiceAreaTypeEnum,
  SidePanelTypeEnum,
} from "@app/access-reports/admin";
import { AccessReportProviderSetModel } from "@app/provider-sets";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { cloneDeep } from "lodash";
import { FlyoutLink } from "@app/shared/Flyouts";
import { Modal } from "@app/shared/Modals";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { WafflesClassNames } from "@app/shared/waffles/exports";
import { JSX } from "react";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons"; 
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import ProviderSetService from "@app/provider-sets/providerSetService";
import ServiceAreaService from "@app/service-areas/serviceAreaService";
import { ServiceArea } from "@app/service-areas/serviceArea";
import { AccessReportsServiceArea } from "@app/access-reports/shared/accessReportsServiceArea";
import { AccessReportsWizardAccessCriteria } from "@app/access-reports/shared/accessReportsWizardAccessCriteria";
import { AccessReportsWizardAnalysisTypeEnum } from "@app/access-reports/shared/accessReportsWizardAnalysisTypeEnum";
import { AccessReportsWizardNetworkStandard } from "@app/access-reports/shared/accessReportsWizardNetworkStandard";
import { AccessReportsWizardNetworkStandardTypeEnum } from "@app/access-reports/shared/accessReportsWizardNetworkStandardTypeEnum";
import { AccessReportsWizardOption } from "@app/access-reports/shared/accessReportsWizardOption";
import { AccessReportsWizardProduct } from "@app/access-reports/shared/accessReportsWizardProduct";

interface AccessReportsAdminAddProductSpecialtiesProps {
  disableUserActions: boolean;
  filterMemberSetByOutsideServiceArea: boolean;
  groups: string[];
  onAddSpecialtiesClick: (specialties: string[]) => void;
  onFilterMemberSetByOutsideServiceAreaToggle: () => void;
  onFilterProviderSetByOutsideServiceAreaToggle: (accessCriteriaId: number) => void;
  onGroupToggle: (accessCriteriaId: number, group: string) => void;
  onMemberSetServiceAreaDropdownChange: (serviceArea: AccessReportsServiceArea) => void;
  onNetworkStandardChange: (
    accessCriteria: AccessReportsWizardAccessCriteria,
    networkStandardType: AccessReportsWizardNetworkStandardTypeEnum,
    standardKey: keyof AccessReportsWizardNetworkStandard,
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onNetworkStandardMilesChange: (
    accessCriteria: AccessReportsWizardAccessCriteria,
    networkStandardType: AccessReportsWizardNetworkStandardTypeEnum,
    standardKey: keyof AccessReportsWizardNetworkStandard,
    event?: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onProviderGroupChange: (accessCriteria: AccessReportsWizardAccessCriteria, providerSetModel: AccessReportProviderSetModel) => void;
  onProviderSetServiceAreaDropdownChange: (product: AccessReportsWizardProduct) => void;
  onRemoveSpecialtyClick: (accessCriteria: AccessReportsWizardAccessCriteria) => void;
  onSaveSpecialtiesClick: (specialties: AccessReportsWizardOption[]) => void;
  onSaveSpecialtyNameClick: (accessCriteriaId: number, accessCriteriaName: string) => void;
  product: AccessReportsWizardProduct;
  selectedMemberSetServiceArea: AccessReportsServiceArea;
}

interface ProductSpecialtiesTableData {
  [wafflesKeyColumnName]: string;
  blank: JSX.Element;
  count: JSX.Element;
  miles: JSX.Element;
  minutes: JSX.Element;
  type: string;
}

const AccessReportsAdminAddProductSpecialties: React.FC<AccessReportsAdminAddProductSpecialtiesProps> = (props: AccessReportsAdminAddProductSpecialtiesProps) => {
  const providerSetService = React.useRef(new ProviderSetService()).current;
  const serviceAreaService = React.useRef(new ServiceAreaService()).current;
  const deleteSpecialtyModalTriggers = React.useRef<{ [id: number]: React.RefObject<HTMLButtonElement> }>({});
  const [groupsDropdownVisible, setGroupsDropdownVisible] = React.useState(false);
  const [providerSets, setProviderSets] = React.useState<AccessReportProviderSetModel[]>([]);
  const [providerSetsLoading, setProviderSetsLoading] = React.useState(true);
  const [serviceAreas, setServiceAreas] = React.useState<ServiceArea[]>([]);
  const [serviceAreasLoading, setServiceAreasLoading] = React.useState(true);
  const [specialtyToRemove, setSpecialtyToRemove] = React.useState<AccessReportsWizardAccessCriteria>(null);
  const [visibleSidePanel, setVisibleSidePanel] = React.useState<SidePanelTypeEnum>(null);

  React.useEffect(() => {
    getProviderSets();
    getServiceAreas();

    return () => {
      document.body.removeEventListener("click", onMouseClick);
    }
  }, []);

  React.useEffect(() => {
    if (groupsDropdownVisible) {
      document.body.addEventListener("click", onMouseClick);
    } else {
      document.body.removeEventListener("click", onMouseClick);
    }
  }, [groupsDropdownVisible]);

  React.useEffect(() => {
    if (Object.entries(deleteSpecialtyModalTriggers.current).length === 0) {
      deleteSpecialtyModalTriggers.current = [];
      for (let i = 0; i < props.product.accessCriterion.length; i += 1) {
        deleteSpecialtyModalTriggers.current[props.product.accessCriterion[i].id] = React.createRef<HTMLButtonElement>();
      }
    }
  }, [props.product.accessCriterion]);

  const getProviderSummary = (accessCriteria: AccessReportsWizardAccessCriteria): JSX.Element => {
    return (
      <React.Fragment>
        <div className={WafflesClassNames.headRow}>
          <div className={`${WafflesClassNames.th} no-sort`}>
            <div className="horizontal-header-title waffles-head-content" style={{ height: "100%" }}>
              <span className="waffles-head-title d-inline-block">
                Provider Summary
              </span>
            </div>
          </div>
        </div>

        <div className="row form-group align-items-center mt-3 ps-3">
          <label className="col-md-2 label-s text-start">
            Provider Group
            <span className="text-danger">*</span>
          </label>
          <div className="col-md-3">
            {!providerSetsLoading ? (
              providerSets?.length ? (
                <QESSelect
                  allowEmpty={true}
                  disabled={props.disableUserActions}
                  hideLabel={true}
                  isValid={!!accessCriteria.providerSet?.providerSetId}
                  testId={`access-reports-admin__add-product--provider-sets-${accessCriteria.id}`}
                  label="Provider Group"
                  required={true}
                  onChange={(e) => onProviderGroupChange(accessCriteria, e)}
                  value={(accessCriteria.providerSet?.providerSetId) || ""}
                >
                  {providerSets.map((providerSet, providerSetIndex) => (
                    <option
                      key={providerSetIndex}
                      value={providerSet.providerSetId}
                    >
                      {providerSet.name}
                    </option>
                  ))}
                </QESSelect>
              ) : (
                <label className="col-form-label text-start fw-normal">
                  <QesIcon iconName={QesIcons.AlertHexagon} description="Error" className="text-danger me-1 mb3" />
                  No provider groups available
                </label>
              )
            ) : (
              <div className="text-center">
                <LoadingSpinner />
              </div>
            )}
          </div>
        </div>

        <div className="row form-group align-items-center ps-3">
          {buildServiceAreaDropdown(ServiceAreaTypeEnum.ProviderSet, accessCriteria.id)}

          {!props.product.skipAccessAnalysis && (
            <div className="col-md-5">
              <QESCheckbox
                checked={getFilterProviderSetByOutsideServiceAreaValue(accessCriteria.id)}
                disabled={props.disableUserActions}
                label={"Filter Provider Set by Outside Service Area"}
                testId={`access-reports-admin__add-product--filter-provider-set-checkbox-${accessCriteria.id}`}
                onChange={() => props.onFilterProviderSetByOutsideServiceAreaToggle(accessCriteria.id)}
              />
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }

  const hideSidePanel = (): void => {
    if (!!visibleSidePanel) {
      setVisibleSidePanel(null);
    }
  }

  const onAddSpecialtiesClick = (specialties: string[]): void => {
    props.onAddSpecialtiesClick(specialties);
    hideSidePanel();
  }

  const onSaveSpecialtyNameClick = (accessCriteriaId: number, accessCriteriaName: string): void => {
    props.onSaveSpecialtyNameClick(accessCriteriaId, accessCriteriaName);
    hideSidePanel();
  }

  const buildNetworkStandardSection = (accessCriteria: AccessReportsWizardAccessCriteria): React.ReactNode => {
    const standard: AccessReportsWizardNetworkStandard[] = [];
    const dynamicByZipClass: AccessReportsWizardNetworkStandard[] = [];
    const dyanmicByCountyClass: AccessReportsWizardNetworkStandard[] = [];

    for (const networkStandard of accessCriteria.standards) {
      switch (networkStandard.type) {
        case AccessReportsWizardNetworkStandardTypeEnum.Standard:
          standard.push(networkStandard);
          break;
        case AccessReportsWizardNetworkStandardTypeEnum.ZipUrban:
        case AccessReportsWizardNetworkStandardTypeEnum.ZipSuburban:
        case AccessReportsWizardNetworkStandardTypeEnum.ZipRural:
          dynamicByZipClass.push(networkStandard);
          break;
        case AccessReportsWizardNetworkStandardTypeEnum.CountyLarge:
        case AccessReportsWizardNetworkStandardTypeEnum.CountyMetro:
        case AccessReportsWizardNetworkStandardTypeEnum.CountyMicro:
        case AccessReportsWizardNetworkStandardTypeEnum.CountyRural:
        case AccessReportsWizardNetworkStandardTypeEnum.CountyCEAC:
          dyanmicByCountyClass.push(networkStandard);
          break;
        default:
          break;
      }
    }

    return (
      <React.Fragment>
        {buildTable(accessCriteria, standard, AccessReportsWizardAnalysisTypeEnum.Standard)}
        {buildTable(accessCriteria, dynamicByZipClass, AccessReportsWizardAnalysisTypeEnum.DynamicByZipClass)}
        {buildTable(accessCriteria, dyanmicByCountyClass, AccessReportsWizardAnalysisTypeEnum.DynamicByCountyClass)}
      </React.Fragment>
    );
  }

  const buildServiceAreaDropdown = (serviceAreaType: ServiceAreaTypeEnum, accessCriteriaId?: number) => {
    let title = "";
    switch (serviceAreaType) {
      case ServiceAreaTypeEnum.MemberSet:
        title = "Member Set";
        break;
      case ServiceAreaTypeEnum.ProviderSet:
        title = "Provider Set";
        break;
      default:
        break;
    }

    return (
      <React.Fragment>
        <div className="col-md-2">
          <label className="label-s mb-0">
            {title} Service Area
          </label>
        </div>

        <div className="col-md-3">
          {!serviceAreasLoading ? (
            serviceAreas?.length ? (
              <QESSelect
                allowEmpty={true}
                disabled={props.disableUserActions}
                testId={`access-reports-admin__add-product--${serviceAreaType}-service-area-dropdown-${accessCriteriaId}`}
                label=""
                onChange={(e) => onServiceAreaDropdownChange(e, serviceAreaType, accessCriteriaId)}
                value={getServiceAreaDropdownValue(serviceAreaType, accessCriteriaId)}
              >
                {serviceAreas.map((serviceArea, index) => (
                  <option
                    key={index}
                    value={serviceArea.serviceAreaId}
                  >
                    {!!serviceArea.resource ? serviceArea.resource.name : serviceArea.serviceAreaId}
                  </option>
                ))}
              </QESSelect>
            ) : (
              <label className="col-form-label text-start fw-normal">
                <QesIcon iconName={QesIcons.AlertHexagon} description="Error" className="text-danger me-1 mb3" />
                No service areas available
              </label>
            )
          ) : (
            <div className="text-center">
              <LoadingSpinner />
            </div>
          )}
        </div>
      </React.Fragment>
    );
  }

  const buildTable = (accessCriteria: AccessReportsWizardAccessCriteria, standards: AccessReportsWizardNetworkStandard[], analysisType: AccessReportsWizardAnalysisTypeEnum): React.ReactNode => {
    const columnConfig: ColumnConfig = {
      blank: {
        displayText: "",
        noSort: true,
        testId: "blank",
      },
      count: {
        displayText: "Count",
        noSort: true,
        testId: "count",
      },
      miles: {
        displayText: "Miles",
        noSort: true,
        testId: "miles",
      },
      minutes: {
        displayText: "Minutes",
        noSort: true,
        testId: "minutes",
      },
      type: {
        displayText: splitKey(AccessReportsWizardAnalysisTypeEnum[analysisType]),
        noSort: true,
        testId: "type",
      },
    };

    const data: ProductSpecialtiesTableData[] = standards.map((standard, index) => ({
      /* oxlint-disable sort-keys */
      [wafflesKeyColumnName]: standard.type,
      type: standard.type.split(/([A-Z]{1}[a-z]+)([A-Z]+.*)/).join(" "),
      count: (
        <QESTextInput
          testId={`access-reports-admin__add-product--min-count-${accessCriteria.id}-${index}`}
          disabled={props.disableUserActions}
          label=""
          onBlur={() => props.onNetworkStandardChange(accessCriteria, standard.type, "minCount")}
          onChange={(event) => props.onNetworkStandardChange(accessCriteria, standard.type, "minCount", event)}
          value={standard.minCount}
        />
      ),
      miles: (
        <QESTextInput
          testId={`access-reports-admin__add-product--miles-${accessCriteria.id}-${index}`}
          disabled={props.disableUserActions}
          label=""
          onBlur={() => props.onNetworkStandardMilesChange(accessCriteria, standard.type, "miles")}
          onChange={(event) => props.onNetworkStandardMilesChange(accessCriteria, standard.type, "miles", event)}
          value={standard.miles}
        />
      ),
      minutes: (
        <QESTextInput
          testId={`access-reports-admin__add-product--minutes-${accessCriteria.id}-${index}`}
          disabled={props.disableUserActions}
          label=""
          onBlur={() => props.onNetworkStandardChange(accessCriteria, standard.type, "minutes")}
          onChange={(event) => props.onNetworkStandardChange(accessCriteria, standard.type, "minutes", event)}
          value={standard.minutes}
        />
      ),
      blank: <>&nbsp;</>,
      /* oxlint-enable */
    }));
    return (
      <div className="access-reports-wizard-admin-table">
        <Waffles<ProductSpecialtiesTableData>
          columns={columnConfig}
          data={data}
          disableProcessingOverlay={true}
          disableStickyHeader={true}
          fullWidth={true}
          testId={`access-reports-admin-add-product-specialties-${analysisType}`}
        />
      </div>
    );
  }

  const displayRemoveSpecialtyAlert = (accessCriteria: AccessReportsWizardAccessCriteria): void => {
    setSpecialtyToRemove(accessCriteria);
  }

  const getFilterProviderSetByOutsideServiceAreaValue = (accessCriteriaId: number) => {
    if (!!props.product && accessCriteriaId !== null && accessCriteriaId !== undefined) {
      const accessCriteria: AccessReportsWizardAccessCriteria = props.product.accessCriterion.find(x => x.id === accessCriteriaId);

      if (!!accessCriteria) {
        return accessCriteria.filterProviderSetByOutsideServiceArea;
      }
    }

    return false;
  }

  const getProviderSets = (): void => {
    providerSetService.getAccessReportingProviderSets().then((newProviderSets: AccessReportProviderSetModel[]) => {
      setProviderSets(newProviderSets);
      setProviderSetsLoading(false);
    });
  }

  const getServiceAreaDropdownValue = (serviceAreaType: ServiceAreaTypeEnum, accessCriteriaId?: number): string => {
    switch (serviceAreaType) {
      case ServiceAreaTypeEnum.MemberSet:
        if (!!props.selectedMemberSetServiceArea &&
          props.selectedMemberSetServiceArea.serviceAreaId !== null &&
          props.selectedMemberSetServiceArea.serviceAreaId !== undefined
        ) {
          return props.selectedMemberSetServiceArea.serviceAreaId.toString();
        }

        break;
      case ServiceAreaTypeEnum.ProviderSet:
        if (!!props.product && accessCriteriaId !== null && accessCriteriaId !== undefined) {
          const accessCriteria: AccessReportsWizardAccessCriteria = props.product.accessCriterion.find(x => x.id === accessCriteriaId);

          if (!!accessCriteria &&
            !!accessCriteria.providerSetServiceArea &&
            accessCriteria.providerSetServiceArea.serviceAreaId !== null &&
            accessCriteria.providerSetServiceArea.serviceAreaId !== undefined
          ) {
            return accessCriteria.providerSetServiceArea.serviceAreaId.toString();
          }
        }

        break;
      default:
        break;
    }

    return "";
  }

  const getServiceAreas = async (): Promise<void> => {
    serviceAreaService.getServiceAreas(false).then((newServiceAreas: ServiceArea[]) => {
      setServiceAreas(newServiceAreas);
      setServiceAreasLoading(false);
    });
  }

  const hideRemoveSpecialtyAlert = (removeSpecialtyConfirmed: boolean): void => {
    if (removeSpecialtyConfirmed) {
      props.onRemoveSpecialtyClick(specialtyToRemove);
    }

    setSpecialtyToRemove(null);
  }

  const onProviderGroupChange = (accessCriteria: AccessReportsWizardAccessCriteria, event: React.ChangeEvent<HTMLSelectElement>): void => {
    const providerSet: AccessReportProviderSetModel = providerSets.find(x => x.providerSetId.toString() === event.target.value);

    if (!!providerSet) {
      props.onProviderGroupChange(accessCriteria, providerSet);
    }
  }

  const onSaveSpecialtiesClick = (specialties: AccessReportsWizardOption[]): void => {
    props.onSaveSpecialtiesClick(specialties);
    hideSidePanel();
  }

  const onServiceAreaDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>, serviceAreaType: ServiceAreaTypeEnum, accessCriteriaId?: number): void => {
    const selectedServiceArea: ServiceArea = serviceAreas.find(x => x.serviceAreaId.toString() === event.target.value);

    let serviceArea: AccessReportsServiceArea = null;
    if (!!selectedServiceArea) {
      serviceArea = {
        serviceAreaId: selectedServiceArea.serviceAreaId,
        serviceAreaName: selectedServiceArea.resource ? selectedServiceArea.resource.name : selectedServiceArea.serviceAreaId.toString(),
        serviceAreaVersionId: 0,
      };
    }

    switch (serviceAreaType) {
      case ServiceAreaTypeEnum.MemberSet:
        props.onMemberSetServiceAreaDropdownChange(serviceArea);
        break;
      case ServiceAreaTypeEnum.ProviderSet:
        if (!!props.product && accessCriteriaId !== null && accessCriteriaId !== undefined) {
          const product: AccessReportsWizardProduct = cloneDeep(props.product);
          const accessCriteria: AccessReportsWizardAccessCriteria = product.accessCriterion.find(x => x.id === accessCriteriaId);

          if (!!accessCriteria) {
            accessCriteria.providerSetServiceArea = serviceArea;

            if (!!serviceArea &&
              serviceArea.serviceAreaId !== null &&
              serviceArea.serviceAreaId !== undefined
            ) {
              accessCriteria.filterProviderSetByServiceArea = true;
            } else {
              accessCriteria.filterProviderSetByServiceArea = false;
            }

            props.onProviderSetServiceAreaDropdownChange(product);
          }
        }

        break;
      default:
        break;
    }
  }

  const onMouseClick = (e: MouseEvent): void => {
    const target: HTMLElement = e.target as HTMLElement;

    if (target.closest(".checkbox-dropdown-container") === null && groupsDropdownVisible) {
      setGroupsDropdownVisible(false);
    }
  }

  const renderAddSpecialtiesSidePanel = (): React.ReactNode => {
    return (
      <AccessReportsAdminAddProductAddSpecialties
        hideSidePanel={hideSidePanel}
        onAddSpecialtiesClick={onAddSpecialtiesClick}
        product={props.product}
      />
    );
  }

  const renderEditSpecialtyNameSidePanel = (specialty: AccessReportsWizardAccessCriteria): React.ReactNode => {
    return (
      <EditSpecialtyName
        hideSidePanel={hideSidePanel}
        onSaveSpecialtyNameClick={onSaveSpecialtyNameClick}
        product={props.product}
        specialty={specialty}
      />
    );
  }

  const renderManageSpecialtiesSidePanel = (): React.ReactNode => {
    return (
      <ManageOptions
        disableAddingNewOptions={true}
        disableDeletion={true}
        disableUserActions={props.disableUserActions}
        onSaveOptionsClick={onSaveSpecialtiesClick}
        options={props.product.accessCriterion as AccessReportsWizardOption[]}
        optionType="specialty"
        requireMinimumOne={true}
      />
    );
  }

  const makeGroupsDropdownVisible = (): void => {
    setGroupsDropdownVisible(true);
  }

  const splitKey = (key: string): string => {
    return key.replace(/([A-Z])/g, " $1");
  }

  const hasSpecialties: boolean = props.product?.accessCriterion.length > 0;

  return (
    <React.Fragment>
      {props.product.skipAccessAnalysis ? (
        getProviderSummary(props.product?.accessCriterion[0])
      ) : (
        <React.Fragment>
          <Modal
            isVisible={!!specialtyToRemove}
            content={{
              body: `Are you sure you want to delete ${specialtyToRemove?.name}?`,
              cancelButtonText: "Cancel",
              confirmButtonAction: () => hideRemoveSpecialtyAlert(true),
              confirmButtonText: "Delete",
              confirmButtonType: "primary-danger",
              title: "Warning",
            }}
            closeModal={() => hideRemoveSpecialtyAlert(false)}
            hideBodyScroll={true}
            triggerElement={deleteSpecialtyModalTriggers.current[specialtyToRemove?.id]}
          />

          <div className="row">
            <div className="col-12">
              <div className="mb-0">
                {hasSpecialties &&
                  <h4 className="header-s me-3">
                    All ({props.product.accessCriterion.length})
                  </h4>
                }

                {!props.disableUserActions ? (
                  <div>
                    <FlyoutLink
                      classNames="qes-button-secondary py-0"
                      content={{
                        body: renderAddSpecialtiesSidePanel() as JSX.Element,
                        title: "Add Specialties",
                      }}
                      panelId="access-reports-admin__add-product--add-specialty"
                      isVisible={visibleSidePanel === SidePanelTypeEnum.AddSpecialty}
                      size="20"
                      testId="access-reports-admin__add-product--add-specialty-link"
                    >
                      Add Specialty
                    </FlyoutLink>
                    <span className="mx-3">
                      |
                    </span>
                    <FlyoutLink
                      classNames="qes-button-secondary py-0"
                      content={{
                        body: renderManageSpecialtiesSidePanel() as JSX.Element,
                        title: "Manage Specialties",
                      }}
                      panelId="access-reports-admin__add-product--manage-specialties"
                      isVisible={visibleSidePanel === SidePanelTypeEnum.ManageSpecialties}
                      size="20"
                      testId="access-reports-admin__add-product--manage-specialties-link"
                    >
                      Manage Specialties
                    </FlyoutLink>

                  </div>

                ) : (
                  <span
                    className="qes-button-secondary disabled text-muted"
                  >
                    Add Specialty
                  </span>
                )}
              </div>
            </div>
          </div>

          {hasSpecialties ? (
            <React.Fragment>
              <div className="row">
                <div className="col-12">
                  <hr />
                </div>
              </div>

              <div className="row align-items-center">
                {buildServiceAreaDropdown(ServiceAreaTypeEnum.MemberSet)}

                <div className="col-md-3">
                  <QESCheckbox
                    checked={props.filterMemberSetByOutsideServiceArea}
                    disabled={props.disableUserActions}
                    label={"Filter Member Set by Outside Service Area"}
                    testId={`access-reports-admin__add-product--filter-member-set-checkbox`}
                    onChange={() => props.onFilterMemberSetByOutsideServiceAreaToggle()}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <hr />
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <AccordionContainer>
                    {props.product.accessCriterion.map((accessCriteria, accessCriteriaIndex) => (
                      <Accordion
                        key={accessCriteriaIndex}
                        title={accessCriteria.name}
                        testId={`access-reports-admin-add-product-specialties__${accessCriteria.name}-accordion`}
                      >
                        <div className="row">
                          <div className="col-12">
                            <hr className="mt-0" />
                          </div>
                        </div>

                        <div className="form-group row align-items-center">
                          {(props.groups && props.groups.length > 0) &&
                            <React.Fragment>
                              <label className="col-md-1 label-s text-start">
                                Group(s)
                              </label>

                              <div className="col-md-3">
                                <div className="position-relative checkbox-dropdown-container">
                                  <QESButton
                                    disabled={props.disableUserActions}
                                    qesTestId={`access-reports-admin__add-product--groups-${accessCriteria.id}`}
                                    qesType="secondary"
                                    className="d-flex h-100 w-100 align-items-center justify-content-between access-reports-groups-btn text-dark text-decoration-none"
                                    onClick={() => makeGroupsDropdownVisible()}
                                  >
                                    <span>
                                      {(accessCriteria.groups && accessCriteria.groups.length > 0) ?
                                        accessCriteria.groups.join(", ") :
                                        "\u00A0"
                                      }
                                    </span>
                                    <span aria-hidden="true"><QesIcon iconName={QesIcons.ChevronDown} description="Expand" /></span>
                                  </QESButton>
                                  {groupsDropdownVisible && (
                                    <div className="checkbox-dropdown mt-0 w-100">
                                      {props.groups.map((group, groupIndex) => (
                                        <QESCheckbox
                                          containerClassName="checkbox-dropdown__label cursor-pointer"
                                          label={group}
                                          checked={accessCriteria.groups && accessCriteria.groups.includes(group)}
                                          disabled={props.disableUserActions}
                                          className="checkbox-dropdown__checkbox"
                                          id={`access-reports-admin__add-product--groups-checkbox-${accessCriteria.id}`}
                                          onChange={() => props.onGroupToggle(accessCriteria.id, group)}
                                          testId={`access-reports-add-product-specialties__${group}`}
                                          key={groupIndex}
                                        />
                                      ))}

                                    </div>
                                  )}
                                </div>
                              </div>
                            </React.Fragment>
                          }

                          <div className={`col-md-${(props.groups && props.groups.length > 0) ? "8" : "12"}`}>
                            <span className="float-end">
                              <FlyoutLink
                                classNames="qes-button-secondary"
                                content={{
                                  body: renderEditSpecialtyNameSidePanel(accessCriteria) as JSX.Element,
                                  title: "Edit Specialty Name",
                                }}
                                disabled={props.disableUserActions}
                                panelId={`access-reports-admin__add-product--edit-specialty-name-${accessCriteria.id}`}
                                isVisible={visibleSidePanel === SidePanelTypeEnum.EditSpecialtyName}
                                size="20"
                                testId={`access-reports-admin__add-product--edit-specialty-name-${accessCriteria.id}-link`}
                              >
                                Edit Specialty Name
                              </FlyoutLink>

                              <span className="text-muted ms-3 me-3">
                                |
                              </span>
                              <QESButton
                                disabled={props.disableUserActions}
                                qesTestId={`access-reports-admin__add-product--remove-specialty-${accessCriteria.id}`}
                                qesType="secondary-danger"
                                onClick={() => displayRemoveSpecialtyAlert(accessCriteria)}
                                qesRef={deleteSpecialtyModalTriggers.current[accessCriteria.id]}
                              >
                                Remove Specialty
                              </QESButton>
                            </span>
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-12">
                            {buildNetworkStandardSection(accessCriteria)}
                          </div>
                        </div>

                        {getProviderSummary(accessCriteria)}
                      </Accordion>
                    ))}
                  </AccordionContainer>
                </div>
              </div>
            </React.Fragment>
          ) : (
            <div className="row">
              <div className="col-12">
                <p className="text-center">
                  There are no specialties to display
                </p>
              </div>
            </div>
          )}
        </React.Fragment>
      )}

    </React.Fragment>
  );
}

export { AccessReportsAdminAddProductSpecialties };
